import React, { useMemo, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Edit from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { useFirestoreResources } from '../../Hooks/useFetchResources';
import { unifiedFirebaseService } from '../../services/firebaseService';

function firestoreDocId(res) {
  return String(res.id);
}

function sortSiblings(list) {
  return [...list].sort((a, b) => {
    const d = (a.order || 0) - (b.order || 0);
    if (d !== 0) return d;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function childrenOf(flat, parentKey) {
  if (parentKey === '__root__') {
    return sortSiblings(flat.filter((r) => !r.parentId));
  }
  return sortSiblings(
    flat.filter(
      (r) =>
        r.parentId === parentKey ||
        String(r.parentId) === String(parentKey)
    )
  );
}

function nextOrderForSiblings(siblings) {
  const max = siblings.reduce((m, r) => Math.max(m, Number(r.order) || 0), -1);
  return max + 1;
}

/** Total nested resources that list this id as parent (recursive). */
function countDescendants(flat, rootId) {
  const sid = String(rootId);
  const kids = flat.filter(
    (r) => r.parentId === rootId || String(r.parentId) === sid
  );
  return kids.reduce((sum, c) => sum + 1 + countDescendants(flat, c.id), 0);
}

/**
 * Calendar column order is driven by Firestore `resources.order`.
 * Reorder within each group (top-level columns, then children under each parent).
 */
export default function ResourceOrderEditor() {
  const flat = useFirestoreResources();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { topLevel, childGroups, byId } = useMemo(() => {
    const idMap = new Map(flat.map((r) => [String(r.id), r]));
    const parentKeys = new Set();
    flat.forEach((r) => {
      if (r.parentId != null && r.parentId !== '') {
        parentKeys.add(String(r.parentId));
      }
    });
    const top = childrenOf(flat, '__root__');
    top.forEach((t) => parentKeys.add(String(t.id)));

    const groups = [...parentKeys]
      .map((pk) => {
        const parent = idMap.get(pk);
        const kids = childrenOf(flat, pk);
        return {
          key: pk,
          title: parent?.name ? `Under “${parent.name}”` : `Under id ${pk}`,
          children: kids,
        };
      })
      .sort((a, b) => {
        const pa = idMap.get(a.key);
        const pb = idMap.get(b.key);
        const oa = pa?.order ?? 0;
        const ob = pb?.order ?? 0;
        if (oa !== ob) return oa - ob;
        return a.title.localeCompare(b.title);
      });
    return { topLevel: top, childGroups: groups, byId: idMap };
  }, [flat]);

  const swapOrders = useCallback(async (a, b) => {
    const idA = firestoreDocId(a);
    const idB = firestoreDocId(b);
    const oa = a.order ?? 0;
    const ob = b.order ?? 0;
    await unifiedFirebaseService.updateDoc('resources', idA, { order: ob });
    await unifiedFirebaseService.updateDoc('resources', idB, { order: oa });
  }, []);

  const moveInList = useCallback(
    async (siblings, index, dir) => {
      const j = dir === 'up' ? index - 1 : index + 1;
      if (j < 0 || j >= siblings.length) return;
      setBusy(true);
      setError(null);
      try {
        await swapOrders(siblings[index], siblings[j]);
      } catch (e) {
        console.error('[ResourceOrderEditor]', e);
        setError(e?.message || 'Failed to update order');
      } finally {
        setBusy(false);
      }
    },
    [swapOrders]
  );

  const closeDialog = useCallback(() => {
    setDialog(null);
    setNameInput('');
  }, []);

  const openEditDialog = useCallback((res) => {
    setError(null);
    setDialog({ mode: 'edit', res });
    setNameInput(res.name || '');
  }, []);

  const openAddDialog = useCallback((parentKey, siblings) => {
    setError(null);
    setDialog({
      mode: 'add',
      parentKey,
      nextOrder: nextOrderForSiblings(siblings),
    });
    setNameInput('');
  }, []);

  const saveDialog = useCallback(async () => {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      setError('Name is required.');
      return;
    }
    if (!dialog) return;

    setBusy(true);
    setError(null);
    try {
      if (dialog.mode === 'edit') {
        const id = firestoreDocId(dialog.res);
        await unifiedFirebaseService.updateDoc('resources', id, { name: trimmed });
      } else {
        const parent = dialog.parentKey === '__root__' ? null : byId.get(String(dialog.parentKey));
        const parentId =
          dialog.parentKey === '__root__'
            ? null
            : parent != null
              ? parent.id
              : dialog.parentKey;

        const payload = { name: trimmed, order: dialog.nextOrder };
        if (dialog.parentKey !== '__root__' && parentId != null && parentId !== '') {
          payload.parentId = parentId;
        }
        await unifiedFirebaseService.addDoc('resources', payload);
      }
      closeDialog();
    } catch (e) {
      console.error('[ResourceOrderEditor]', e);
      setError(e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }, [dialog, nameInput, byId, closeDialog]);

  const openDeleteConfirm = useCallback((res) => {
    setError(null);
    setConfirmDelete({ res });
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    if (!busy) setConfirmDelete(null);
  }, [busy]);

  const performDelete = useCallback(async () => {
    if (!confirmDelete) return;
    setBusy(true);
    setError(null);
    try {
      await unifiedFirebaseService.deleteDoc(
        'resources',
        firestoreDocId(confirmDelete.res)
      );
      setConfirmDelete(null);
    } catch (e) {
      console.error('[ResourceOrderEditor]', e);
      setError(e?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }, [confirmDelete]);

  const deleteDescendantCount = useMemo(() => {
    if (!confirmDelete) return 0;
    return countDescendants(flat, confirmDelete.res.id);
  }, [confirmDelete, flat]);

  const renderRow = (res, siblings, index) => (
    <Box
      key={firestoreDocId(res)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        px: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap title={res.name}>
          {res.name || '(no name)'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          id {firestoreDocId(res)} · order {res.order ?? 0}
        </Typography>
      </Box>
      <IconButton
        size="small"
        aria-label="Rename resource"
        disabled={busy}
        onClick={() => openEditDialog(res)}
      >
        <Edit fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color="error"
        aria-label="Delete resource"
        disabled={busy}
        onClick={() => openDeleteConfirm(res)}
      >
        <DeleteOutline fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Move up"
        disabled={busy || index === 0}
        onClick={() => moveInList(siblings, index, 'up')}
      >
        <KeyboardArrowUp fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Move down"
        disabled={busy || index === siblings.length - 1}
        onClick={() => moveInList(siblings, index, 'down')}
      >
        <KeyboardArrowDown fontSize="small" />
      </IconButton>
    </Box>
  );

  const sectionHeader = (title, onAdd, addLabel) => (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Typography variant="subtitle2">{title}</Typography>
      <IconButton
        size="small"
        aria-label={addLabel}
        disabled={busy}
        onClick={onAdd}
      >
        <Add fontSize="small" />
      </IconButton>
    </Box>
  );

  if (!flat.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No resources loaded yet. Open this page while signed in with calendar access.
      </Typography>
    );
  }

  const dialogTitle =
    dialog?.mode === 'edit'
      ? 'Rename resource'
      : dialog?.mode === 'add'
        ? dialog.parentKey === '__root__'
          ? 'New top-level resource'
          : 'New resource under parent'
        : '';

  return (
    <Box sx={{ mt: 3, maxWidth: 560 }}>
      <Typography variant="h6" gutterBottom>
        Resource column order
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Use the arrows to reorder, the pen to rename, the trash icon to delete (with confirmation), and + on a section header to add a resource in that group.
        Changes apply to the live calendar after Firestore syncs.
      </Typography>
      {error && !dialog && !confirmDelete ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ mb: 2 }}>
        {sectionHeader('Top-level columns', () => openAddDialog('__root__', topLevel), 'Add top-level resource')}
        <Divider />
        {topLevel.map((res, i) => renderRow(res, topLevel, i))}
      </Paper>

      {childGroups.map(({ key, title, children }) => (
        <Paper key={key} variant="outlined" sx={{ mb: 2 }}>
          {sectionHeader(title, () => openAddDialog(key, children), 'Add resource in this group')}
          <Divider />
          {children.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.5 }}>
              No child resources yet. Use + to add one.
            </Typography>
          ) : (
            children.map((res, i) => renderRow(res, children, i))
          )}
        </Paper>
      ))}

      <Dialog open={Boolean(dialog)} onClose={busy ? undefined : closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          {error && dialog ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) {
                e.preventDefault();
                void saveDialog();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void saveDialog()} variant="contained" disabled={busy}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={busy ? undefined : closeDeleteConfirm}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete resource?</DialogTitle>
        <DialogContent>
          {error && confirmDelete ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}
          <Typography variant="body2">
            Delete “
            {confirmDelete?.res?.name ||
              (confirmDelete?.res ? firestoreDocId(confirmDelete.res) : '…')}
            ” (id {confirmDelete?.res ? firestoreDocId(confirmDelete.res) : '…'})? This cannot be undone.
          </Typography>
          {deleteDescendantCount > 0 ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This resource has {deleteDescendantCount} nested resource
              {deleteDescendantCount === 1 ? '' : 's'} underneath it. They will not be deleted
              automatically and may disappear from the calendar until you fix their parent.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => void performDelete()}
            variant="contained"
            color="error"
            disabled={busy}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
